import { get, post } from 'models/shared/helpers/axiosInstance';
import { LoanPreOffer } from 'models/financial-form/interfaces/financial-form.interfaces';
import { VehiclePurposeEnum } from 'models/financial-form/enums/financial-form.enums';
import { CreditPolicy } from '../interfaces/credit-policy.interface';

export interface LoanOffer {
	duration: number;
	downPaymentPct: number;
	downPaymentAmount: number;
	ticketPrice: number;
	financingAmount: number;
	periodicPayment: number;
	paymentFrequency: string;
	expirationDate: string;
	currency: string;
	createdAt: string;
	monthlyIncome: number;
	maxMonthlyCapacity: number;
	maxPeriodicCapacity: number;
	maxPaymentCapacity: number;
	maxFinancingCapacity: number;
	commercialUse: boolean;
	periodicRate: number;
	periodicRateWithTax: number;
	periodicBasePayment: number;
	periodicExtraPayment: number;
	totalInsuranceAmount: number;
	totalGpsAmount: number;
	totalExtraAmount: number;
	policyVersion: string;
	profileTier: string;
}

export interface CreditLineOffer
	extends Omit<
		LoanOffer,
		| 'monthlyIncome'
		| 'maxMonthlyCapacity'
		| 'maxPeriodicCapacity'
		| 'maxPaymentCapacity'
		| 'maxFinancingCapacity'
	> {
	profileTier: string;
}

export interface LoanOfferResult {
	status: 'approved' | 'rejected';
	policyVersion: string;
	currency: string;
	offers: LoanOffer[];
	minDownPaymentAmount?: number;
	maxDownPaymentAmount?: number;
	reason?: string;
}

export interface CreditLineOfferResult {
	status: 'approved' | 'rejected';
	policyVersion: string;
	currency: string;
	offers: CreditLineOffer[];
	minDownPaymentAmount?: number;
	maxDownPaymentAmount?: number;
	reason?: string;
}

export interface PreviewPreOfferResult {
	valid: boolean;
	reason: string | null;
	preOffer: LoanPreOffer;
}

export const getActiveCreditPolicy = async (): Promise<CreditPolicy> => {
	try {
		const res = await get<CreditPolicy>('/v1/loan-offer/policy/active');
		return res.data;
	} catch (err: any) {
		throw err.response?.data ?? err;
	}
};

export const calculateOffersFromFinancialForm = async (
	financialFormId: string,
	dto: {
		ticketPrice: number;
		downPaymentAmount?: number;
		loanDuration?: number;
		vehiclePurpose?: VehiclePurposeEnum;
	},
): Promise<LoanOfferResult> => {
	try {
		const res = await post<LoanOfferResult>(
			`/v1/loan-offer/calculate/financial-form/${financialFormId}/admin`,
			dto,
		);
		return res.data;
	} catch (err: any) {
		throw err.response?.data ?? err;
	}
};

export const calculateOffersForCreditLine = async (
	financialFormId: string,
	dto: {
		ticketPrice: number;
		downPaymentAmount?: number;
		loanDuration?: number;
		vehiclePurpose?: VehiclePurposeEnum;
	},
): Promise<CreditLineOfferResult> => {
	try {
		const res = await post<CreditLineOfferResult>(
			`/v1/loan-offer/calculate/financial-form/${financialFormId}/credit-line`,
			dto,
		);
		return res.data;
	} catch (err: any) {
		throw err.response?.data ?? err;
	}
};

export const previewPreOffer = async (
	financialFormId: string,
	dto: { monthlyVerifiedIncome: number; monthlyVerifiedOutcome?: number },
): Promise<PreviewPreOfferResult> => {
	try {
		const res = await post<PreviewPreOfferResult>(
			`/v1/loan-offer/calculate/financial-form/${financialFormId}/preview`,
			dto,
		);
		return res.data;
	} catch (err: any) {
		throw err.response?.data ?? err;
	}
};
